import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface UpgradeOverlayProps {
    /** Titolo alternativo (default: "Funzionalità Premium") */
    title?: string;
    /** Messaggio che spiega con quale piano si sblocca la funzionalità */
    description?: string;
}

const UpgradeOverlay: React.FC<UpgradeOverlayProps> = ({ title, description }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <div className="flex-1 h-full min-h-[60vh] flex flex-col items-center justify-center p-8 text-center bg-gray-50/50 rounded-3xl border border-gray-100 m-4">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="max-w-md space-y-6"
            >
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mb-6 flex justify-center"
                >
                    <div className="relative">
                        <div className="w-20 h-20 bg-clip-100 rounded-2xl flex items-center justify-center rotate-3 shadow-lg">
                            <Lock size={40} className="text-clip-600" />
                        </div>
                        <motion.div
                            animate={{ rotate: [0, 15, 0] }}
                            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                            className="absolute -top-2 -right-2 bg-yellow-400 text-white p-2 rounded-full shadow-md"
                        >
                            <Sparkles size={16} fill="currentColor" />
                        </motion.div>
                    </div>
                </motion.div>

                <div className="space-y-2">
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                        {title || t('upgrade.title', 'Funzionalità Premium')}
                    </h2>
                    <p className="text-gray-500 leading-relaxed">
                        {description || t('upgrade.desc_generic', 'Questa funzionalità non è inclusa nel tuo piano. Effettua l\'upgrade per sbloccarla.')}
                    </p>
                </div>

                <div className="pt-4">
                    <button
                        onClick={() => navigate('/subscription')}
                        className="group relative inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-clip-500 text-white font-bold rounded-xl shadow-lg shadow-clip-500/25 hover:bg-clip-600 hover:shadow-clip-500/40 transition-all overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            <Zap size={18} className="fill-current" />
                            {t('upgrade.cta', 'Upgrade Ora')}
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default UpgradeOverlay;
